import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { SectionTitle } from "@/components/GlassBits";
import { formatINR, formatDate, statusBadgeClass } from "@/lib/format";
import { Search, Plus, X, FileText, RotateCw } from "lucide-react";

const blankForm = {
  customer_email: "", customer_name: "", phone: "", amount: "",
  method: "upi", purpose: "", category: "", branch: "",
  payment_date: new Date().toISOString().slice(0, 10), reference: "",
};

function Field({ label, children }) {
  return <label className="block min-w-0">
    <span className="block text-[10px] tracking-[0.2em] uppercase text-white/50 mb-1.5">
      {label}
    </span>
    {children}
  </label>;
}

function SignaturePad({ onChange, value = '' }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const hasInk = useRef(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = "#111827";
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  }, []);

  // Restore the saved signature whenever the parent state changes.
  // This prevents a React re-render/remount from leaving a blank canvas.
  useEffect(() => {
    if (!value || !value.startsWith("data:image/png;base64,")) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const image = new Image();
    image.onload = () => {
      const currentCanvas = canvasRef.current;
      if (!currentCanvas) return;

      const ctx = currentCanvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, currentCanvas.width, currentCanvas.height);
      ctx.drawImage(image, 0, 0, currentCanvas.width, currentCanvas.height);
      hasInk.current = true;
    };
    image.src = value;

    return () => {
      image.onload = null;
    };
  }, [value]);

  const point = (event) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return {
      x: (event.clientX - rect.left) * canvas.width / rect.width,
      y: (event.clientY - rect.top) * canvas.height / rect.height,
    };
  };

  const start = (event) => {
    event.preventDefault();
    const canvas = canvasRef.current;
    canvas.setPointerCapture?.(event.pointerId);
    const p = point(event);
    const ctx = canvas.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    drawing.current = true;
  };

  const move = (event) => {
    if (!drawing.current) return;
    event.preventDefault();
    const p = point(event);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    hasInk.current = true;
  };

  const stop = () => {
    if (!drawing.current) return;
    drawing.current = false;
    onChange(hasInk.current ? canvasRef.current.toDataURL("image/png") : "");
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    hasInk.current = false;
    onChange("");
  };

  return <div>
    <canvas
      ref={canvasRef}
      width={600}
      height={180}
      onPointerDown={start}
      onPointerMove={move}
      onPointerUp={stop}
      onPointerCancel={stop}
      className="w-full rounded-md border border-white/20 bg-white touch-none"
      style={{ touchAction: "none" }}
      aria-label="Draw payment signature"
      data-testid="mp-signature-canvas"
    />
    <div className="flex justify-between items-center mt-2">
      <span className="text-xs text-white/50">Draw your signature above</span>
      <button type="button" onClick={clear} className="text-xs underline text-white/70">
        Clear signature
      </button>
    </div>
  </div>;
}

export default function ManualPayments() {
  const [items, setItems] = useState([]);
  const [branches, setBranches] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [method, setMethod] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(blankForm);
  const [proof, setProof] = useState(null);
  const [review, setReview] = useState(null);
  const [reviewSignature, setReviewSignature] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [reviewBusy, setReviewBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = { limit: 500 };
      if (q.trim()) params.q = q.trim();
      if (status) params.status = status;
      if (method) params.method = method;
      const { data } = await api.get("/admin/manual-payments", { params });
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load manual payments");
    }
  }, [q, status, method]);

  useEffect(() => {
    const timer = setTimeout(load, 250);
    return () => clearTimeout(timer);
  }, [load]);

  useEffect(() => {
    api.get("/admin/branches")
      .then(({ data }) => setBranches(Array.isArray(data) ? data : []))
      .catch(() => toast.error("Could not load branches"));
  }, []);

  const setValue = (key, value) => setForm((old) => ({ ...old, [key]: value }));

  const submit = async (event) => {
    event.preventDefault();
    if (!Number.isFinite(Number(form.amount)) || Number(form.amount) <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    setBusy(true);
    try {
      const body = new FormData();
      Object.entries(form).forEach(([key, value]) => {
        body.append(key, key === "payment_date" ? `${value}T12:00:00` : value);
      });
      if (proof) body.append("proof", proof);
      await api.post("/admin/manual-payments", body);
      toast.success("Payment recorded and sent for approval");
      setOpen(false);
      setForm(blankForm);
      setProof(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit manual payment");
    } finally {
      setBusy(false);
    }
  };

  const openPrivateFile = async (item, kind) => {
    try {
      const response = await api.get(
        `/admin/manual-payments/${encodeURIComponent(item.manual_payment_id)}/files/${kind}`,
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(response.data);
      const tab = window.open(url, "_blank", "noopener,noreferrer");
      if (!tab) {
        const link = document.createElement("a");
        link.href = url;
        link.download = `${item.manual_payment_id}-${kind}`;
        link.click();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error(err.response?.data?.detail || `Could not open ${kind}`);
    }
  };

  const startReview = (item) => {
    setReview(item);
    setReviewSignature("");
    setRejectReason("");
  };

  const approvePayment = async () => {
    if (!review) return;
    if (!reviewSignature) {
      toast.error("Draw the admin signature before approving");
      return;
    }

    setReviewBusy(true);
    try {
      const body = new FormData();
      body.append("signature_data_url", reviewSignature);

      await api.post(
        `/admin/manual-payments/${encodeURIComponent(review.manual_payment_id)}/approve`,
        body
      );

      toast.success("Payment approved successfully");
      setReview(null);
      setReviewSignature("");
      setRejectReason("");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not approve payment");
    } finally {
      setReviewBusy(false);
    }
  };

  const rejectPayment = async () => {
    if (!review) return;

    const reason = rejectReason.trim();
    if (!reason) {
      toast.error("Enter a reason for rejecting this payment");
      return;
    }

    setReviewBusy(true);
    try {
      const body = new FormData();
      body.append("reason", reason);

      await api.post(
        `/admin/manual-payments/${encodeURIComponent(review.manual_payment_id)}/reject`,
        body
      );

      toast.success("Payment rejected");
      setReview(null);
      setReviewSignature("");
      setRejectReason("");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not reject payment");
    } finally {
      setReviewBusy(false);
    }
  };

  return <div>
    <SectionTitle
      eyebrow="Ledger · Offline receipts"
      title="Manual Payments"
      right={<button onClick={() => setOpen(true)}
        className="ldc-btn-primary px-4 py-2 rounded-md text-sm inline-flex items-center gap-2"
        data-testid="mp-new">
        <Plus size={16} /> Record payment
      </button>}
    />

    <div className="ldc-glass p-3 mb-4 flex flex-wrap gap-3 items-center">
      <div className="relative flex-1 min-w-[200px]">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
        <input value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search customer, email, reference or ID"
          className="ldc-input w-full pl-9 pr-3 py-2 rounded-md text-sm" />
      </div>
      <select value={status} onChange={(e) => setStatus(e.target.value)}
        className="ldc-input rounded-md px-3 py-2 text-sm">
        <option value="">All statuses</option>
        <option value="pending">Pending approval</option>
        <option value="approved">Approved</option>
        <option value="rejected">Rejected</option>
      </select>
      <select value={method} onChange={(e) => setMethod(e.target.value)}
        className="ldc-input rounded-md px-3 py-2 text-sm">
        <option value="">All methods</option>
        <option value="upi">UPI</option>
        <option value="cash">Cash</option>
      </select>
      <button type="button" onClick={load} title="Refresh"
        className="p-2 rounded-md border border-white/10"><RotateCw size={16}/></button>
    </div>

    <div className="ldc-glass overflow-hidden">
      <div className="overflow-x-auto ldc-scroll">
        <table className="w-full ldc-table" data-testid="mp-table">
          <thead><tr>
            <th>Payment / Customer</th><th>Branch / Purpose</th><th>Amount</th>
            <th>Method</th><th>Payment date</th><th>Status</th><th>Proof / Actions</th>
          </tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-white/40">No manual payments found.</td></tr>}
            {items.map((it) => <tr key={it.manual_payment_id}>
              <td>
                <div className="font-mono text-xs text-[#fbce18]">{it.manual_payment_id}</div>
                <div className="mt-1">{it.customer_name || "Customer"}</div>
                <div className="text-xs text-white/45">{it.customer_email}</div>
                {it.reference && <div className="text-xs text-white/45">Ref: {it.reference}</div>}
              </td>
              <td>
                <div>{it.branch || "—"}</div>
                <div className="text-xs text-white/50">{it.purpose}</div>
                {it.category && <div className="text-xs text-white/40">{it.category}</div>}
              </td>
              <td className="font-medium">{formatINR(it.amount, "INR")}</td>
              <td className="uppercase text-xs">{it.method || "—"}</td>
              <td className="text-xs text-white/50">{formatDate(it.payment_date)}</td>
              <td>
                <span className={statusBadgeClass(it.status)}>{it.status}</span>
                {it.rejection_reason && <div className="text-xs text-red-300 mt-1">{it.rejection_reason}</div>}
                {it.approved_by && <div className="text-xs text-white/40 mt-1">By {it.approved_by}</div>}
              </td>
              <td>
                <div className="flex flex-wrap gap-1">
                  {it.has_proof && <button type="button" onClick={() => openPrivateFile(it, "proof")}
                    className="px-2 py-1 rounded border border-white/10 text-xs inline-flex items-center gap-1">
                    <FileText size={12}/> Proof
                  </button>}
                  {it.has_signature && <button type="button" onClick={() => openPrivateFile(it, "signature")}
                    className="px-2 py-1 rounded border border-white/10 text-xs">Signature</button>}
                  {it.status === "pending" && <button type="button" onClick={() => startReview(it)}
                    className="px-2 py-1 rounded border border-[#fbce18]/40 text-[#fbce18] text-xs">
                    Review
                  </button>}
                </div>
                <div className="text-[10px] text-white/40 mt-1">{formatDate(it.created_at)}</div>
              </td>
            </tr>)}
          </tbody>
        </table>
      </div>
    </div>

    {open && <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4 overflow-y-auto"
      onClick={() => !busy && setOpen(false)}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
        className="ldc-glass p-5 md:p-6 w-full max-w-2xl my-6">
        <div className="flex justify-between items-center mb-5">
          <div><div className="text-[10px] uppercase tracking-[0.2em] text-[#fbce18]/80">Offline transaction</div>
            <h3 className="font-display text-xl">Record Manual Payment</h3></div>
          <button type="button" onClick={() => setOpen(false)} className="p-2"><X size={18}/></button>
        </div>
        <p className="text-xs text-white/50 mb-4">Record the payment for admin review. It will remain pending until approved or rejected. Receipt optional.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Customer email *"><input required type="email" maxLength={254}
            value={form.customer_email} onChange={(e) => setValue("customer_email", e.target.value)}
            className="ldc-input w-full rounded-md px-3 py-2 text-sm"/></Field>
          <Field label="Customer name"><input value={form.customer_name} maxLength={200}
            onChange={(e) => setValue("customer_name", e.target.value)}
            className="ldc-input w-full rounded-md px-3 py-2 text-sm"/></Field>
          <Field label="Phone"><input value={form.phone} maxLength={40}
            onChange={(e) => setValue("phone", e.target.value)}
            className="ldc-input w-full rounded-md px-3 py-2 text-sm"/></Field>
          <Field label="Amount (INR) *"><input required type="number" min="0.01" max="100000000" step="0.01"
            value={form.amount} onChange={(e) => setValue("amount", e.target.value)}
            className="ldc-input w-full rounded-md px-3 py-2 text-sm"/></Field>
          <Field label="Payment method *"><select required value={form.method}
            onChange={(e) => setValue("method", e.target.value)}
            className="ldc-input w-full rounded-md px-3 py-2 text-sm">
            <option value="upi">UPI</option><option value="cash">Cash</option>
          </select></Field>
          <Field label="Branch *"><select required value={form.branch}
            onChange={(e) => setValue("branch", e.target.value)}
            className="ldc-input w-full rounded-md px-3 py-2 text-sm">
            <option value="">Select branch</option>
            {branches.map((b) => <option key={b.id || b.name} value={b.name}>{b.name}</option>)}
          </select></Field>
          <Field label="Payment date *"><input required type="date" value={form.payment_date}
            onChange={(e) => setValue("payment_date", e.target.value)}
            className="ldc-input w-full rounded-md px-3 py-2 text-sm"/></Field>
          <Field label="Reference / UPI transaction ID"><input value={form.reference} maxLength={200}
            onChange={(e) => setValue("reference", e.target.value)}
            className="ldc-input w-full rounded-md px-3 py-2 text-sm"/></Field>
          <Field label="Category"><select value={form.category}
            onChange={(e) => setValue("category", e.target.value)}
            className="ldc-input w-full rounded-md px-3 py-2 text-sm">
            <option value="">Select category</option>
            {["General", "Subscription", "Product", "Service", "Consulting", "Other"].map((c) =>
              <option key={c} value={c}>{c}</option>
            )}
          </select></Field>
          <Field label="Purpose *"><input required value={form.purpose} maxLength={500}
            onChange={(e) => setValue("purpose", e.target.value)}
            className="ldc-input w-full rounded-md px-3 py-2 text-sm"/></Field>
          <div className="md:col-span-2">
            <Field label="Proof / receipt (optional; JPG, PNG, WEBP or PDF; max 8 MB)">
              <input type="file" accept=".jpg,.jpeg,.png,.webp,.pdf,image/jpeg,image/png,image/webp,application/pdf"
                onChange={(e) => setProof(e.target.files?.[0] || null)}
                className="ldc-input w-full rounded-md px-3 py-2 text-sm"/>
            </Field>
            {proof && <div className="text-xs text-white/50 mt-1">{proof.name} · {(proof.size / 1024 / 1024).toFixed(2)} MB</div>}
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button type="button" disabled={busy} onClick={() => setOpen(false)}
            className="px-4 py-2 rounded-md border border-white/10 text-sm">Cancel</button>
          <button disabled={busy} type="submit"
            className="ldc-btn-primary px-5 py-2 rounded-md text-sm">{busy ? "Saving…" : "Record payment"}</button>
        </div>
      </form>
    </div>}


      {review && <div
        className="fixed inset-0 z-[60] bg-black/75 flex items-center justify-center p-4 overflow-y-auto"
        onClick={() => !reviewBusy && setReview(null)}
      >
        <div
          className="ldc-glass p-5 md:p-6 w-full max-w-2xl my-6"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex justify-between items-start gap-3 mb-5">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#fbce18]/80">
                Pending payment review
              </div>
              <h3 className="font-display text-xl">Review Manual Payment</h3>
              <div className="text-xs text-white/50 mt-1">
                {review.manual_payment_id}
              </div>
            </div>
            <button
              type="button"
              disabled={reviewBusy}
              onClick={() => setReview(null)}
              className="p-2"
              aria-label="Close review"
            >
              <X size={18}/>
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
            <div>
              <div className="text-xs text-white/50">Customer</div>
              <div>{review.customer_name || "Customer"}</div>
              <div className="text-xs text-white/60 break-all">{review.customer_email}</div>
            </div>
            <div>
              <div className="text-xs text-white/50">Amount</div>
              <div className="font-semibold text-lg">{formatINR(review.amount, "INR")}</div>
            </div>
            <div>
              <div className="text-xs text-white/50">Branch / Method</div>
              <div>{review.branch || "—"} · {(review.method || "—").toUpperCase()}</div>
            </div>
            <div>
              <div className="text-xs text-white/50">Payment date</div>
              <div>{formatDate(review.payment_date)}</div>
            </div>
            <div className="sm:col-span-2">
              <div className="text-xs text-white/50">Purpose</div>
              <div className="whitespace-pre-wrap">{review.purpose || "—"}</div>
              {review.reference && <div className="text-xs text-white/50 mt-1">
                Reference: {review.reference}
              </div>}
              {review.category && <div className="text-xs text-white/50 mt-1">
                Category: {review.category}
              </div>}
            </div>
          </div>

          <div className="mb-5">
            {review.has_proof ? (
              <button
                type="button"
                onClick={() => openPrivateFile(review, "proof")}
                className="px-3 py-2 rounded-md border border-white/15 text-sm inline-flex items-center gap-2"
              >
                <FileText size={14}/> Open payment proof
              </button>
            ) : (
              <p className="text-xs text-amber-200">No receipt or proof was attached.</p>
            )}
          </div>

          <div className="mb-5">
            <Field label="Admin signature required to approve">
              <SignaturePad value={reviewSignature} onChange={setReviewSignature}/>
            </Field>
          </div>

          <div className="mb-5">
            <Field label="Rejection reason (required to reject)">
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                maxLength={1000}
                rows={3}
                placeholder="Explain why this payment is being rejected"
                className="ldc-input w-full rounded-md px-3 py-2 text-sm"
              />
            </Field>
          </div>

          <div className="flex flex-wrap justify-end gap-2">
            <button
              type="button"
              disabled={reviewBusy}
              onClick={() => setReview(null)}
              className="px-4 py-2 rounded-md border border-white/10 text-sm"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={reviewBusy || !rejectReason.trim()}
              onClick={rejectPayment}
              className="px-4 py-2 rounded-md border border-red-400/40 text-red-200 text-sm disabled:opacity-50"
            >
              {reviewBusy ? "Processing…" : "Reject payment"}
            </button>
            <button
              type="button"
              disabled={reviewBusy || !reviewSignature}
              onClick={approvePayment}
              className="ldc-btn-primary px-5 py-2 rounded-md text-sm disabled:opacity-50"
            >
              {reviewBusy ? "Processing…" : "Sign & Approve"}
            </button>
          </div>
        </div>
      </div>}

  </div>;
}
